import { useEffect, useRef, useState } from 'react';
import { ILoggedInAccessState } from '../types/rbac/ILoggedInAccessState';
import { PUBLIC_UNGATED_ACCESS } from '../store/access/AccessContext';
import { tokenService } from '../services/TokenService';
import { useGetMenus } from './graphql/useGetMenus';
import useGetCompany from './graphql/useGetCompany';
import { flattenMenuPaths } from '../utils/role-based-access/flattenMenuPaths';

export const useLoggedInAccess = (): ILoggedInAccessState => {
  const token = tokenService.getToken();
  const platformId = tokenService.getPlatformId() || 1;
  const currentRole = tokenService.getUserRole();

  const { getUserMenus } = useGetMenus();
  const { getCompanyProfile } = useGetCompany();

  const [state, setState] = useState<ILoggedInAccessState>(() => {
    if (!tokenService.getToken()) {
      return PUBLIC_UNGATED_ACCESS;
    }
    return {
      status: 'loading',
      menuGuardEnabled: true,
      allowedPaths: [],
      rolePermissions: [],
      menus: [],
    };
  });

  const resolvedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      resolvedForRef.current = null;
      setState(PUBLIC_UNGATED_ACCESS);
      return;
    }

    let active = true;

    const resolveAccess = async () => {
      try {
        const companyRes = await getCompanyProfile();
        if (!companyRes?.data?.getCompany?.data) {
          throw new Error('Invalid company session');
        }

        const resolveKey = `${currentRole?.roleId ?? 0}_${platformId}_${token}`;
        if (resolvedForRef.current === resolveKey) {
          return;
        }
        resolvedForRef.current = resolveKey;

        const menusRes = await getUserMenus({
          variables: {
            params: {
              platformId,
            },
          },
          fetchPolicy: 'no-cache',
        });

        const menuData = menusRes?.data?.getUserMenus?.data;
        const userMenus = menuData?.mainMenus || [];
        const rolePermissions = menuData?.rolePermissions || [];

        if (!active) return;

        tokenService.setUserMenus(userMenus);
        tokenService.setUserPermissions(rolePermissions);

        setState({
          status: 'ready',
          menuGuardEnabled: true,
          allowedPaths: flattenMenuPaths(userMenus),
          rolePermissions,
          menus: userMenus,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to resolve logged-in access:', error);
        resolvedForRef.current = null;

        if (!active) return;

        tokenService.disposeAllTokens();
        setState(PUBLIC_UNGATED_ACCESS);
      }
    };

    void resolveAccess();

    return () => {
      active = false;
    };
  }, [token, platformId, currentRole?.roleId, getUserMenus, getCompanyProfile]);

  return state;
};

export default useLoggedInAccess;
